import { useDeleteSuperAdminBabackScriptTags } from "@/api/super-admin/use-super-admin-storefront-embed";
import type { StorefrontEmbedAuditDto } from "@babackapp/private-api-client/generated/schema";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@babackapp/ui/components/alert-dialog";
import { type MouseEvent, type ReactNode, useState } from "react";
import type { EmbedShop } from "./storefront-embed-row-filter";

export function StorefrontEmbedDeleteDialog({
  shop,
  audit,
  children,
}: {
  shop: EmbedShop;
  audit: StorefrontEmbedAuditDto;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const { mutate, isPending, error, reset } =
    useDeleteSuperAdminBabackScriptTags(shop.id);

  const handleOpenChange = (next: boolean) => {
    if (isPending) return;
    setOpen(next);
    if (!next) reset();
  };

  const confirm = (event: MouseEvent) => {
    // AlertDialogAction would close the dialog before the request settles.
    event.preventDefault();
    mutate(undefined, { onSuccess: () => handleOpenChange(false) });
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Supprimer les script tags Baback de {shop.name} ?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-destructive">
                Action irréversible : depuis le 1er octobre 2026, Shopify
                interdit de recréer un script tag. Après suppression, le widget
                sur la boutique ne dépendra plus que de l&apos;app embed. Si le
                marchand désactive l&apos;embed, le widget disparaîtra.
              </p>
              <ul className="list-disc space-y-1 pl-5 break-all text-xs">
                {audit.babackScriptTags.map((tag) => (
                  <li key={tag.id}>{tag.src}</li>
                ))}
              </ul>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error.message}
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Annuler</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={isPending}
            onClick={confirm}
          >
            {isPending ? "Suppression…" : "Supprimer définitivement"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
